import {filter, first, intersection, isNil, map, size, sortBy, transform, uniq, clone, Dictionary} from 'lodash';
import {action, computed, makeObservable, observable, toJS} from 'mobx';

import {generateLocalId, makeUniqueNodeLabel} from '../../cablingMapEditor/utils';
import {DUAL_ATTACHMENT_TYPE, FIRST_SWITCH_PEER, LAG_MODES, PEER_LINKS_TYPES, PORT_ROLES, SECOND_SWITCH_PEER,
  SINGLE_ATTACHMENT_TYPE} from '../const';
import {stringFromSpeed} from '../utils';
import {ctrlDirections, ctrlEntityTypes, ctrlHalfNodeHeight, ctrlHalfNodeWidth, ctrlIfcPositions, ctrlNodeHeight,
  ctrlNodeWidth, ctrlPortSpacing} from '../../cablingMapEditor/const';
import Port from './Port';
import RackStore from './RackStore';
import Node from './Node';
import {IEntity} from '../../cablingMapEditor/store/ChangesStore';
import {LinksGroupPeeringType, PortRoleType, SpeedType, WritableKeys} from '../types';
import {ISerializable} from '../../cablingMapEditor/types';

export type LinksGroupPropertiesType = WritableKeys<LinksGroup>;
export type LinksGroupPropertiesObject = Partial<Record<LinksGroupPropertiesType, any>>;

class LinksGroup implements ISerializable {
  @observable id: string = generateLocalId();
  @observable label = '';
  @observable fromName = '';
  @observable toName = '';
  @observable count = 0;
  @observable speed = null;
  @observable isAttachedToFirst = true;
  @observable isAttachedToSecond = true;
  @observable tags = [];
  @observable peeringType: LinksGroupPeeringType | null = null;
  @observable railIndex: number | null = null;
  @observable isDraft = false;

  @observable _lagMode = LAG_MODES.NO_LAG;

  @observable.ref rackStore: RackStore;

  @observable stage = null;

  get type() {
    return ctrlEntityTypes.LINKS_GROUP;
  }

  @computed
  get isInRail() {
    return !isNil(this.railIndex);
  }

  @computed
  get isPeer() {
    return this.fromName === this.toName;
  }

  @computed
  get isL3Peer() {
    return this.isPeer && this.peeringType === PEER_LINKS_TYPES.L3_PEER;
  }

  @computed
  get isDualAttached() {
    return this.isAttachedToFirst && this.isAttachedToSecond && this.isToPair;
  }

  @computed
  get speedString() {
    return stringFromSpeed(this.speed);
  }

  @computed
  get fromRole(): PortRoleType | null {
    return this.isPeer ? PORT_ROLES.PEER : (this.fromNode?.role ?? null);
  }

  @computed
  get toRole(): PortRoleType | null {
    return this.isPeer ? PORT_ROLES.PEER : (this.toNode?.role ?? null);
  }

  @computed
  get fromNode(): Node | undefined {
    return first(this.rackStore?.nodesByName[this.fromName]);
  }

  @computed
  get toNode(): Node | undefined {
    return first(this.rackStore.nodesByName[this.toName]);
  }

  @computed
  get nodesInvolved() {
    return this.isPeer ?
      this.fromNode?.nodesInvolved :
      [...this.fromNode?.nodesInvolved ?? [], ...this.toNode?.nodesInvolved ?? []];
  }

  @computed
  get isToPair() {
    return this.toNode?.isPaired;
  }

  @computed
  get isBetweenPairs() {
    return this.fromNode?.isPaired && this.isToPair && !this.isPeer;
  }

  @computed
  get portsPositions() {
    return transform(
      map(this.nodesInvolved),
      (acc, node) => {
        if (node) acc[node.id] = findPortPosition(this, node);
      },
      {}
    );
  }

  @computed
  get lagMode() {
    return ((this.count > 1 || this.isDualAttached) && this._lagMode === LAG_MODES.NO_LAG) ?
      LAG_MODES.LACP_ACTIVE : this._lagMode;
  }

  set lagMode(mode) {
    this.setProperty('_lagMode', mode);
  }

  @computed
  get hasAggregation() {
    return this.lagMode !== LAG_MODES.NO_LAG;
  }

  @computed
  get isEmpty() {
    return !(this.count > 0);
  }

  constructor(rackStore: RackStore) {
    makeObservable(this);
    this.rackStore = rackStore;
  }

  @action
  makeDraft() {
    this.isDraft = true;
  }

  @action
  setProperty(name: string, value: any) {
    this[name] = value;
  }

  // True if some/all of the nodes are contained in this links group
  contains(nodes: Node[], all?: boolean) {
    const names = uniq(map(nodes, 'name'));
    return this.containsNames(names, all);
  }

  // Makes sure that the link connects given two nodes
  connects(node1: Node, node2: Node) {
    if (!this.contains([node1, node2], true)) return false;
    if (this.isDualAttached) return true;
    const [source, destination] = node1.name === this.fromName ? [node1, node2] : [node2, node1];
    return !destination.isPaired || (
      source.isPaired ?
        source.isFirstInPair === destination.isFirstInPair :
        (
          (this.isAttachedToFirst && destination.isFirstInPair) ||
          (this.isAttachedToSecond && !destination.isFirstInPair)
        )
    );
  }

  connectsNames(names: string[]): boolean {
    return names.includes(this.fromName) && names.includes(this.toName);
  }

  // True if some/all of the names are contained in this links group
  containsNames(names: string[], all: boolean = false) {
    if (all && size(names) === 1) {
      return this.fromName === names[0] && this.isPeer;
    }
    const matches = size(intersection([this.fromName, this.toName], names));
    return all ? matches === size(names) : matches > 0;
  }

  oppositeTo(name: string) {
    if (this.fromName === name) return this.toName;
    if (this.toName === name) return this.fromName;
  }

  oppositeToNode({name}: Node) {
    if (this.fromName === name) return this.toNode;
    if (this.toName === name) return this.fromNode;
  }

  // Calculates how many ports the link consumes from each of its nodes
  getSizeOnNodes(nodesByName: Dictionary<Node[]>) {
    const fromNodes = nodesByName[this.fromName];
    const toNodes = nodesByName[this.toName];
    const [fromPair, toPair] = [size(fromNodes) === 2, size(toNodes) === 2];

    // Calculate for source nodes
    const result = transform(fromNodes, (acc, {id}) => {
      acc[id] = this.count * ((toPair && this.isDualAttached) ? 2 : 1);
    }, {});

    // Calculate for destination nodes
    return transform(toNodes, (acc, {id, isFirstInPair}) => {
      acc[id] = this.count * (
        fromPair ?
          (this.isDualAttached ? 2 : 1) :
          (toPair ?
            ((isFirstInPair ? this.isAttachedToFirst : this.isAttachedToSecond) ? 1 : 0) :
            1
          )
      );
    }, result);
  }

  getPortsFor(name: string) {
    const role = this.fromName === name ? this.toRole : this.fromRole;
    return role ? Port.generate(this.count, this.speed, role) : [];
  }

  isSamePeerWith({fromName, isPeer}: LinksGroup) {
    return isPeer && this.containsNames([fromName], true);
  }

  @action
  fillWith(data: Dictionary<any>) {
    Object.assign(this, data);
  }

  @computed
  get propsForCloning() {
    return {
      fromName: this.fromName,
      toName: this.toName,
      label: this.label,
      speed: clone(this.speed),
      count: this.count,
      peeringType: this.peeringType,
      lagMode: this.lagMode,
      tags: [...this.tags],
      isAttachedToFirst: this.isAttachedToFirst,
      isAttachedToSecond: this.isAttachedToSecond,
      railIndex: this.railIndex
    };
  }

  clone() {
    const result = LinksGroup.createFilledWith(this.propsForCloning, this.rackStore);
    result.makeLabelUnique();
    return result;
  }

  @action
  makeLabelUnique() {
    this.label = makeUniqueNodeLabel(this.rackStore.linksGroups, this.label, true);
  }

  cloneFor(namesMap: Dictionary<string>) {
    const result = LinksGroup.createFilledWith({
      ...this.propsForCloning,
      fromName: namesMap?.[this.fromName] ?? this.fromName,
      toName: namesMap?.[this.toName] ?? this.toName,
      // By default railIndex is preserved, but must be changed in case when the leaf gets cloned along with the link
    }, this.rackStore);
    return result;
  }

  serialize(withInternals = true): IEntity {
    return toJS({
      label: this.label,
      tags: toJS(this.tags),
      link_speed: toJS(this.speed),
      link_per_switch_count: this.count,
      rail_index: this.railIndex,
      ...(this.hasAggregation ?
        {lag_mode: this.lagMode} :
        // All links from access switches to leafs must have 'lacp_active' LAG mode
        (this.fromNode?.isAccessSwitch ?
          {lag_mode: LAG_MODES.LACP_ACTIVE} : {}
        )
      ),
      attachment_type: this.isDualAttached ? DUAL_ATTACHMENT_TYPE : SINGLE_ATTACHMENT_TYPE,
      ...((this.isDualAttached || !this.toNode?.isPaired || this.fromNode?.isPaired) ?
        {} :
        {switch_peer: this.isAttachedToFirst ? FIRST_SWITCH_PEER : SECOND_SWITCH_PEER}
      ),
      ...(withInternals ?
        {
          id: this.id,
          internals: {
            id: this.id,
            fromName: this.fromNode?.name,
            toName: this.toNode?.name,
            peeringType: this.peeringType,
            isAttachedToFirst: this.isAttachedToFirst,
            isAttachedToSecond: this.isAttachedToSecond
          }
        } :
        {
          target_switch_label: this.toNode?._label
        }
      )
    }) as IEntity;
  }

  static deserialize(jsonValue, rackStore: RackStore): LinksGroup {
    const {
      label,
      tags,
      node_label: fromName,
      target_switch_label: toName,
      attachment_type: attachmentType,
      switch_peer: switchPeer,
      link_per_switch_count: count,
      lag_mode: lagMode = LAG_MODES.NO_LAG,
      link_speed: speed,
      rail_index: railIndex,
      internals
    } = jsonValue;

    const isDoubleAttached = attachmentType === DUAL_ATTACHMENT_TYPE;
    const isFirst = switchPeer === FIRST_SWITCH_PEER;

    return LinksGroup.createFilledWith({
      label,
      tags,
      speed,
      lagMode,
      fromName,
      toName,
      count,
      railIndex: railIndex,
      isAttachedToFirst: isDoubleAttached || isFirst,
      isAttachedToSecond: isDoubleAttached || !isFirst,
      ...(internals ? internals : {})
    }, rackStore);
  }

  static createPeerLinks(nodesName: string, speed: SpeedType, count: number, rackStore: RackStore,
    peeringType: LinksGroupPeeringType = PEER_LINKS_TYPES.L2_PEER) {
    return LinksGroup.createFilledWith({
      label: `${peeringType}_peer_links`,
      fromName: nodesName,
      toName: nodesName,
      lagMode: LAG_MODES.LACP_ACTIVE,
      speed,
      count,
      peeringType
    }, rackStore);
  }

  static createFilledWith(data: Dictionary<any>, rackStore: RackStore): LinksGroup {
    const result = new LinksGroup(rackStore);
    result.fillWith(data);
    return result;
  }
}

export default LinksGroup;

const findPortPosition = ({id, railIndex}: LinksGroup, node: Node) => {
  const {interfacesPositions, position: {x, y}} = node;

  const [xx, yy] = [x || 0, y || 0];

  const placement = interfacesPositions[id]?.side;
  if (!placement) {
    return {
      x: xx + ctrlHalfNodeWidth,
      y: yy + ctrlHalfNodeHeight
    };
  }

  const sidePorts = uniq(
    map(
      sortBy(
        filter(interfacesPositions, {side: placement}),
        // Making rails come before the link groups
        ({railIndex, node}) => isNil(railIndex) ? node.topologicOrder : railIndex
      ),
      ({id, railIndex}) => isNil(railIndex) ? id : railIndex
    )
  );

  const isInRail = !isNil(railIndex);
  const index = sidePorts.indexOf(isInRail ? railIndex : id);
  const sidePortsCount = size(sidePorts);
  const onShortSide = placement === ctrlIfcPositions.RIGHT || placement === ctrlIfcPositions.LEFT;
  const sideSize = onShortSide ? ctrlNodeHeight : ctrlNodeWidth;
  const spacer = sidePortsCount * ctrlPortSpacing > sideSize ?
    (sideSize - 8) / sidePortsCount :
    ctrlPortSpacing;
  const portsHalfWidth = ((sidePortsCount - 1) * spacer) / 2;

  return onShortSide ?
    {
      x: xx + (placement === ctrlIfcPositions.LEFT ? 0 : ctrlNodeWidth),
      y: yy + ctrlHalfNodeHeight - portsHalfWidth + index * spacer,
      direction: ctrlDirections.HORIZONTAL,
      placement,
      isInRail
    } :
    {
      x: xx + ctrlHalfNodeWidth - portsHalfWidth + index * spacer,
      y: yy + (placement === ctrlIfcPositions.TOP ? 0 : ctrlNodeHeight),
      direction: ctrlDirections.VERTICAL,
      placement,
      isInRail
    };
};
